// src/containers/BookingRequest/components/ReplySection.tsx
import React from "react";
import { PaperClipIcon } from "@heroicons/react/24/outline";
import FileUploadPreview from "./FileUploadPreview";

interface FilePreview {
  type: string;
  content?: string;
  name: string;
  extension?: string;
}

interface ReplySectionProps {
  replyText: string;
  setReplyText: (text: string) => void;
  selectedFiles: File[];
  previews: FilePreview[];
  isSubmitting: boolean;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveFile: (index: number) => void;
  handleSubmit: () => void;
}

const ReplySection: React.FC<ReplySectionProps> = ({
  replyText,
  setReplyText,
  selectedFiles,
  previews,
  isSubmitting,
  handleFileChange,
  handleRemoveFile,
  handleSubmit,
}) => {
  const canSubmit =
    !isSubmitting && (replyText.trim() || selectedFiles.length > 0);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-4 sm:px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-bold text-white">
              Send a Reply
            </h3>
            <p className="text-xs sm:text-sm text-white/80 mt-0.5">
              Communicate with {isSubmitting ? "..." : "the other party"}
            </p>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="p-4 sm:p-6 space-y-4">
        {/* Textarea with enhanced styling */}
        <div className="relative">
          <div className="absolute -top-2 left-3 px-2 bg-white dark:bg-gray-900 z-10">
            <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
              Your Message
            </span>
          </div>
          <textarea
            className="w-full border-2 border-gray-300 dark:border-gray-600 rounded-xl p-4 pt-5
                       min-h-[120px] sm:min-h-[140px]
                       focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                       bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                       placeholder-gray-400 dark:placeholder-gray-500
                       transition-all duration-200 resize-none
                       disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800/50
                       text-sm sm:text-base"
            placeholder="Type your message here... Be clear and professional."
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            disabled={isSubmitting}
            rows={5}
          />

          {/* Character counter and tips */}
          <div className="flex flex-wrap justify-between items-center mt-2 gap-2 px-1">
            <div className="flex items-center gap-2">
              <span
                className={`text-xs font-medium ${
                  replyText.length > 1000
                    ? "text-red-500 dark:text-red-400"
                    : replyText.length > 500
                    ? "text-orange-500 dark:text-orange-400"
                    : "text-gray-500 dark:text-gray-400"
                }`}
              >
                {replyText.length} / 1500 characters
              </span>
              {replyText.length > 1000 && (
                <span className="flex items-center gap-1 text-xs text-red-500 dark:text-red-400">
                  <svg
                    className="w-3 h-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Too long
                </span>
              )}
            </div>
            {replyText.length > 500 && replyText.length <= 1000 && (
              <span className="text-xs text-orange-500 dark:text-orange-400 flex items-center gap-1">
                <svg
                  className="w-3 h-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                Keep it concise
              </span>
            )}
          </div>
        </div>

        {/* File Preview Area */}
        {(selectedFiles.length > 0 || previews.length > 0) && (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-3 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 mb-3">
              <PaperClipIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                Attached Files ({selectedFiles.length})
              </span>
            </div>
            <FileUploadPreview
              selectedFiles={selectedFiles}
              previews={previews}
              onRemoveFile={handleRemoveFile}
            />
          </div>
        )}

        {/* Action Buttons - Mobile Optimized */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          {/* Upload Button */}
          <label
            htmlFor="reply-file-upload"
            className={`
              flex-1 sm:flex-none
              flex items-center justify-center gap-2 px-4 sm:px-6 py-3 sm:py-3.5 rounded-xl
              border-2 border-gray-300 dark:border-gray-600
              bg-white dark:bg-gray-800 
              text-sm font-semibold text-gray-700 dark:text-gray-300
              transition-all duration-200
              ${
                isSubmitting
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:border-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:text-orange-600 dark:hover:text-orange-400 cursor-pointer active:scale-95"
              }
            `}
          >
            <PaperClipIcon className="w-5 h-5 flex-shrink-0" />
            <span className="truncate">
              {selectedFiles.length > 0
                ? `${selectedFiles.length} file${
                    selectedFiles.length > 1 ? "s" : ""
                  } attached`
                : "Attach Files"}
            </span>
            <input
              id="reply-file-upload"
              multiple
              name="reply-file-upload"
              type="file"
              className="sr-only"
              onChange={handleFileChange}
              disabled={isSubmitting}
              accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
            />
          </label>

          {/* Send Button */}
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`
              flex-1 sm:flex-[2]
              flex items-center justify-center gap-2 px-6 py-3 sm:py-3.5 rounded-xl
              text-sm font-bold transition-all duration-200
              ${
                canSubmit
                  ? "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg hover:shadow-xl active:scale-95"
                  : "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
              }
            `}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Sending...</span>
              </>
            ) : (
              <>
                <svg
                  className="w-5 h-5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
                <span>Send Reply</span>
              </>
            )}
          </button>
        </div>

        {/* Helper Text */}
        {!canSubmit && !isSubmitting && (
          <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <svg
              className="w-5 h-5 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-xs sm:text-sm text-blue-700 dark:text-blue-300">
              Write a message or attach files to send a reply to continue the
              conversation.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReplySection;
